<template>
    <section class="py-20">
        <div class="container md:flex">
            <form class="block flex-1" method="post" @submit="onSubmit">
                <slot></slot>

                <input v-if="stripe && totalPrice" type="hidden" name="gatewayId" :value="stripe.id">
                <input v-else type="hidden" name="gatewayId" :value="3">

                <input type="hidden" name="orderEmail" :value="email">

                <div v-if="step === 'address'">
                    <div>
                        <h2 class="h2 mb-4">Email</h2>

                        <LabeledInput
                            v-model="email"
                            label="Email"
                            type="email"
                            name="email"
                            required
                            :disabled="cartLoading"
                            class="px-0 mb-4"
                        />
                    </div>

                    <div>
                        <h2 class="h2 mb-4">Shipping Address</h2>

                        <ul>
                            <li v-for="address in addresses" :key="address.id">
                                <CheckoutAddress v-model="shippingAddressId" :address="address" type="shippingAddress" />
                            </li>

                            <li>
                                <div class="checkout-radio">
                                    <input
                                        id="shippingAddress-new"
                                        v-model="shippingAddressId"
                                        type="radio"
                                        class="sr-only"
                                        name="shippingAddressId"
                                        :value="null"
                                        :disabled="cartLoading"
                                        required
                                    >

                                    <label for="shippingAddress-new" class="flex items-center p-2 bg-secondary-100 mb-2">
                                        <span class="checkout-radio__radio"></span>

                                        <span class="block flex-1 pr-4 text-sm">New Address</span>
                                    </label>
                                </div>
                            </li>
                        </ul>

                        <CheckoutNewAddress
                            v-if="shippingAddressId === null"
                            v-model="shippingAddress"
                            class="mt-2"
                            type="shippingAddress"
                        />
                    </div>

                    <div class="mt-8">
                        <h2 class="h2 mb-4">Billing Address</h2>

                        <div class="mb-4">
                            <div class="fui-checkbox">
                                <input
                                    id="billingAddressSameAsShipping"
                                    v-model="billingAddressSameAsShipping"
                                    type="checkbox"
                                    name="billingAddressSameAsShipping"
                                >

                                <label for="billingAddressSameAsShipping">Use Shipping Address</label>
                            </div>
                        </div>

                        <div v-if="!billingAddressSameAsShipping">
                            <ul>
                                <li v-for="address in addresses" :key="address.id">
                                    <CheckoutAddress v-model="billingAddressId" :address="address" type="billingAddress" />
                                </li>

                                <li>
                                    <div class="checkout-radio">
                                        <input
                                            id="billingAddress-new"
                                            v-model="billingAddressId"
                                            type="radio"
                                            class="sr-only"
                                            name="billingAddressId"
                                            :value="null"
                                            :disabled="cartLoading"
                                            required
                                        >

                                        <label for="billingAddress-new" class="flex items-center p-2 bg-secondary-100 mb-2">
                                            <span class="checkout-radio__radio"></span>

                                            <span class="block flex-1 pr-4 text-sm">New Address</span>
                                        </label>
                                    </div>
                                </li>
                            </ul>

                            <CheckoutNewAddress
                                v-if="billingAddressId === null"
                                v-model="billingAddress"
                                class="mt-2"
                                type="billingAddress"
                            />
                        </div>
                    </div>

                    <div class="flex justify-end mt-8">
                        <button
                            class="btn btn-primary"
                            :class="{ loading }"
                            :disabled="loading"
                        >
                            {{ hasShippingOptions ? 'Proceed to shipping' : 'Proceed to payment' }}
                        </button>
                    </div>
                </div>

                <div v-if="step === 'shipping'">
                    <h2 class="h2 mb-4">Shipping</h2>

                    <ul>
                        <li v-for="method in shippingMethods" :key="method.id">
                            <CheckoutShipping v-model="shippingMethodHandle" :method="method" />
                        </li>
                    </ul>

                    <div class="flex justify-between mt-8">
                        <button
                            type="button"
                            class="btn btn-simple btn-no-icon"
                            :disabled="loading"
                            @click.prevent="step = 'address'"
                        >
                            Shipping/billing address
                        </button>

                        <button
                            class="btn btn-primary"
                            :class="{ loading }"
                            :disabled="loading"
                        >
                            Proceed to payment
                        </button>
                    </div>
                </div>

                <div v-if="step === 'payment'">
                    <h2 class="h2 mb-4">
                        <span v-if="totalPrice">Payment</span>
                        <span v-else>Confirmation</span>
                    </h2>

                    <stripe
                        v-if="stripe && totalPrice"
                        :pk="stripe.pk"
                        :params="stripe.params"
                        @loading="loading = $event"
                    />

                    <div class="flex justify-between mt-8">
                        <button
                            v-if="totalPrice"
                            type="button"
                            class="btn btn-simple btn-no-icon"
                            :disabled="loading"
                            @click.prevent="step = 'shipping'"
                        >
                            Shipping
                        </button>

                        <button
                            class="btn btn-primary"
                            :class="{ loading }"
                            :disabled="loading"
                        >
                            <span v-if="totalPrice">Checkout</span>
                            <span v-else>Place order</span>
                        </button>
                    </div>
                </div>
            </form>

            <div class="md:w-1/3 md:max-w-sm md:ml-20 to_md:mt-12">
                <h2 class="h2 flex items-center mb-4">
                    <span>Cart</span>
                    <span v-if="cartLoading" class="loading ml-6 mt-1" aria-hidden="true"></span>
                </h2>

                <div v-if="items.length">
                    <ul class="-mb-2">
                        <li v-for="line in items" :key="line.id">
                            <CheckoutLine :line="line" />
                        </li>
                    </ul>

                    <div class="mt-8 mb-4">
                        <table class="w-full">
                            <tr>
                                <td class="w-full">Subtotal</td>
                                <td class="text-right">{{ new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD' }).format(itemSubtotal) }}</td>
                            </tr>

                            <tr v-if="totalShippingCost > 0">
                                <td class="w-full">Shipping</td>
                                <td class="text-right">{{ new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD' }).format(totalShippingCost) }}</td>
                            </tr>

                            <tr v-if="totalDiscount > 0">
                                <td class="w-full">Discount</td>
                                <td class="text-right">{{ new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD' }).format(totalDiscount) }}</td>
                            </tr>

                            <tr>
                                <td colspan="2">
                                    <div class="flex space-x-2 pt-4">
                                        <input v-model="couponCode" class="fui-input px-4 py-2" type="text" placeholder="Add discount code" name="couponCode">

                                        <button class="btn btn-primary text-xs pl-4 py-2" @click.prevent="applyCouponCode">Apply</button>
                                    </div>
                                </td>
                            </tr>

                            <tr>
                                <td class="w-full h4 pt-4">Total</td>
                                <td class="text-right pt-4">{{ new Intl.NumberFormat('en-AU', {style: 'currency', currency: 'AUD' }).format(totalPrice) }}</td>
                            </tr>
                        </table>
                    </div>

                    <hr class="mb-4">

                    <div class="richtext text-xs">{{ notice }}</div>
                </div>

                <p v-else>Your cart is empty.</p>
            </div>
        </div>
    </section>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import LabeledInput from '../LabeledInput.vue';
import CheckoutAddress from './CheckoutAddress.vue';
import CheckoutLine from './CheckoutLine.vue';
import CheckoutNewAddress from './CheckoutNewAddress.vue';
import CheckoutShipping from './CheckoutShipping.vue';
import Stripe from './Stripe.vue';
const { mapActions, mapState } = createNamespacedHelpers('cart');

export default {
    components: {
        Stripe,
        CheckoutLine,
        CheckoutAddress,
        CheckoutNewAddress,
        CheckoutShipping,
        LabeledInput,
    },

    props: {
        initialStep: {
            type: String,
            default: 'address',
        },

        notice: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            step: this.initialStep,
            loading: false,

            email: this.orderEmail,
            billingAddress: this.defaultAddress(),
            billingAddressId: this.storeShippingAddressId,
            shippingAddress: this.defaultAddress(),
            shippingAddressId: this.storeBillingAddressId,
            billingAddressSameAsShipping: this.storeShippingAddressId === this.storeBillingAddressId,
            shippingMethodHandle: this.storeShippingMethod || '',
            couponCode: this.orderCouponCode,
        };
    },

    computed: {
        ...mapState({
            items: 'items',
            addresses: 'addresses',
            orderEmail: 'email',
            itemSubtotal: 'itemSubtotal',
            totalShippingCost: 'totalShippingCost',
            totalDiscount: 'totalDiscount',
            totalPrice: 'totalPrice',
            stripe: 'stripe',
            cartLoading: 'loading',
            shippingMethods: 'shippingMethods',
            storeShippingMethod: 'shippingMethod',
            storeShippingAddressId: 'shippingAddressId',
            storeBillingAddressId: 'billingAddressId',
            orderCouponCode: 'couponCode',
        }),
        hasShippingOptions() {
            return this.shippingMethods.length > 1 || !this.storeShippingMethod;
        },
    },

    watch: {
        step(newValue) {
            if (!newValue || newValue === 'address') {
                history.replaceState(null, '', '/shop/checkout');
            } else {
                history.replaceState(null, '', `/shop/checkout/${newValue}`);
            }
        },
        orderEmail(newValue) {
            this.email = newValue;
        },
        orderCouponCode(newValue) {
            this.couponCode = newValue;
        },
        storeShippingAddressId(newValue, oldValue) {
            this.shippingAddressId = newValue;
            if (!oldValue) {
                this.billingAddressSameAsShipping = newValue === this.billingAddressId;
            }
        },
        storeBillingAddressId(newValue, oldValue) {
            this.billingAddressId = newValue;
            if (!oldValue) {
                this.billingAddressSameAsShipping = newValue === this.shippingAddressId;
            }
        },
        storeShippingMethod(newValue) {
            this.shippingMethodHandle = newValue;
        },
    },

    methods: {
        ...mapActions([
            'updateCart',
        ]),

        defaultAddress() {
            return {
                fullName: null,
                addressLine1: null,
                addressLine2: null,
                locality: null,
                postalCode: null,
                countryCode: 13,
                administrativeArea: null,
            };
        },

        onSubmit(event) {
            switch (this.step) {
            case 'address':
                event.preventDefault();
                event.stopPropagation();
                this.onSaveAddress();
                break;
            case 'shipping':
                event.preventDefault();
                event.stopPropagation();
                this.onSaveShipping();
                break;
            }
        },

        async onSaveAddress() {
            this.loading = true;

            const params = {};
            if (this.shippingAddressId) {
                params.shippingAddressId = this.shippingAddressId;
            } else {
                params.shippingAddress = this.shippingAddress;
            }

            if (this.billingAddressSameAsShipping) {
                params.billingAddressSameAsShipping = true;
            } else if (this.billingAddressId) {
                params.billingAddressId = this.billingAddressId;
            } else {
                params.billingAddress = this.billingAddress;
            }

            params.email = this.email;

            await this.updateCart(params);

            this.shippingAddress = this.defaultAddress();
            this.billingAddress = this.defaultAddress();

            this.loading = false;

            if (this.hasShippingOptions) {
                this.step = 'shipping';
            } else {
                this.step = 'payment';
            }
        },

        async onSaveShipping() {
            this.loading = true;

            const params = {
                email: this.email,
                shippingMethodHandle: this.shippingMethodHandle,
            };

            await this.updateCart(params);

            this.loading = false;
            this.step = 'payment';
        },

        async applyCouponCode() {
            this.loading = true;

            const params = {
                couponCode: this.couponCode,
            };

            await this.updateCart(params);

            this.loading = false;
        },
    },
};
</script>
